import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

export interface GetFilterDealershipsParams {
  brands?: string[];
  groups?: string[];
  states?: string[];
  cities?: string[];
  page: number;
  page_size: number;
  search?: string;
  includeInactive?: boolean;
}

export interface DealershipsQueryParams {
  [key: string]: string | string[];
}

@Injectable()
export class DealershipsService {
  constructor(private http: HttpClientService) {}

  /**
   * Builds query params object, filtering out undefined/null/empty values and applying key transformations
   */
  private buildParams<T extends Record<string, any>>(
    params: T,
    keyMappings?: Record<keyof T, string>
  ): DealershipsQueryParams {
    const result: DealershipsQueryParams = {};

    Object.entries(params).forEach(([key, value]) => {
      // Ignore null or undefined
      if (value === null || value === undefined) return;

      // Ignore empty arrays and empty strings.
      if ((Array.isArray(value) && value.length === 0) || (typeof value === "string" && value.trim() === "")) {
        return;
      }

      // Use mapped key if available, otherwise use original key
      const mappedKey = keyMappings?.[key as keyof T] || key;
      result[mappedKey] = value;
    });

    return result;
  }

  public getDealership(id: number, includeInactive?: boolean): Observable<any> {
    const options: { params?: DealershipsQueryParams } = {};
    if (includeInactive) {
      options.params = {
        active: "all",
      };
    }
    return this.http
      .get(`/api/dealerships/${id}`, options)
      .pipe(tap((_) => console.log("DealershipsService.getDealership: Ok.")));
  }

  public combine(original_dealership_id: number, duplicated_dealership_id: number): Observable<any> {
    const url = `/api/dealerships/${original_dealership_id}/combine`;
    const body = {
      duplicated_dealership_id,
    };
    return this.http.post(url, body).pipe(tap(() => console.log(`DealershipsService.combine: Ok.`)));
  }

  public filterDealerships({
    brands = undefined,
    groups = undefined,
    states = undefined,
    cities = undefined,
    postal_code = undefined,
    like = undefined,
    application_id = undefined,
  }): Observable<any> {
    const params = this.buildParams(
      { brands, groups, states, cities, postal_code, like, application_id },
      {
        brands: "brands[]",
        groups: "groups[]",
        states: "states[]",
        cities: "cities[]",
        postal_code: "postal_code[]",
        like: "like",
        application_id: "application_id",
      }
    );

    const options = { params };
    return this.http
      .get("/api/dealerships/filter_dealerships", options)
      .pipe(tap((_) => console.log("DealershipsService.filterDealerships: Ok.")));
  }

  public getDealershipsUsers(id: number | string): Observable<any> {
    return this.http
      .get(`/api/dealerships/${id}/dealerships_users`)
      .pipe(tap((_) => console.log("DealershipsService.show: Ok.")));
  }

  public getDealershipWithTops(id: number | string): Observable<any> {
    return this.http
      .get(`/api/dealerships/${id}/dealership_tops`)
      .pipe(tap((_) => console.log("DealershipsService.getDealershipWithTops: Ok.")));
  }

  public getDealershipScheme(id: number | string, options: { includeInactive?: boolean } = {}): Observable<any> {
    const httpOptions: { params?: DealershipsQueryParams } = {};
    if (options.includeInactive) {
      httpOptions.params = {
        active: "all",
      };
    }
    return this.http
      .get(`/api/dealerships/${id}/dealership_scheme`, httpOptions)
      .pipe(tap((_) => console.log("DealershipsService.getDealershipScheme: Ok.")));
  }

  public postNotifySpecialistClient(id: String | Number, notify: any): Observable<any> {
    let options = {
      notify,
    };
    return this.http
      .post(`/api/dealerships/${id}/notify_specialist_client`, options)
      .pipe(tap((_) => console.log("DealershipsService.postNotifySpecialistClient: Ok.")));
  }

  public requestDealershipApproval(id: String | Number, request: any): Observable<any> {
    let options = {
      request,
    };
    return this.http
      .post(`/api/dealerships/${id}/request_dealership_approval`, options)
      .pipe(tap((_) => console.log("DealershipsService.requestDealershipApproval: Ok.")));
  }


  public getDealershipsFromUser(params: DealershipsQueryParams): Observable<any> {
    const options = {
      params,
    };

    return this.http
      .get("/api/dealerships/dealerships_from_user", options)
      .pipe(tap((_) => console.log("DealershipsService.getDealershipsFromUser: Ok.")));
  }

  public getDealershipWithBankAccounts(id: number | string): Observable<any> {
    return this.http
      .get(`/api/dealerships/${id}/bank_accounts`)
      .pipe(tap((_) => console.log("DealershipsService.getDealershipWithBankAccounts: Ok.")));
  }

  public getDealershipByRfc(rfc: string): Observable<any> {
    const options = {
      params: {
        rfc: rfc,
      } as DealershipsQueryParams,
    };
    return this.http
      .get("/api/dealerships/by_rfc", options)
      .pipe(tap((_) => console.log("DealershipsService.getDealershipByRfc")));
  }

  public getFilterDealerships({
    brands,
    groups,
    states,
    cities,
    page,
    page_size,
    search,
    includeInactive = false,
  }: GetFilterDealershipsParams): Observable<any> {
    const optionalParams = this.buildParams(
      { brands, groups, states, cities, search },
      {
        brands: "brands[]",
        groups: "groups[]",
        states: "states[]",
        cities: "cities[]",
        search: "search",
      }
    );

    const params: DealershipsQueryParams = {
      page_size: page_size.toString(),
      page: page.toString(),
      active: includeInactive ? "all" : "true",
      ...optionalParams,
    };

    const options = { params };
    return this.http
      .get("/api/dealerships/filter_dashboard", options)
      .pipe(tap((_) => console.log("UserService.getFilterDealerships: Ok.")));
  }

  public getFilterInfoDealerships(): Observable<any> {
    return this.http
      .get("/api/dealerships/filter_information")
      .pipe(tap((_) => console.log("UserService.getFilterInfolDealerships: Ok.")));
  }

  public manageTops(data: any): Observable<any> {
    return this.http
      .post("/api/dealerships/manage_tops", data)
      .pipe(tap((_) => console.log("DealershipsService.manageTops: Ok.")));
  }

  public validateManageTops(data: any): Observable<any> {
    return this.http
      .post("/api/dealerships/validate_manage_tops", data)
      .pipe(tap((_) => console.log("DealershipsService.validateManageTops: Ok.")));
  }

  public downloadTopsReport(data: any): Observable<Blob> {
    return this.http.post(`/api/dealerships/tops_report`, data, { responseType: "blob" as "json" });
  }
}
